// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Group, Button, Loader, ActionIcon, Anchor, Breadcrumbs, Divider } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { useElementSize } from '@mantine/hooks';
import { IconEye } from '@tabler/icons';
import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { getUnresolvablePlaceholders } from '../Service/restapi/documentService';
import { ToastS } from '../Service/ToastS';
import { DocumentContext } from '../Service/Context/DocumentContextV2';
import { TestS } from '../TestS';
import { WindowContext } from '../Service/Context/WindowContext';
import DocumentActions from '../Molecules/Document/DocumentActions';
import { Language } from '../Service/DocumentT';
import DocumentLanguagePicker from '../Atoms/inputs/DocumentLanguagePicker';
import EventContractHeaderActions from './Contract/EventContractHeadActions';
import QuestionnaireActions from './Fragebögen/QuestionnaireActions';

const DocumentHeadTemplate: FC<{
  previewUrl: string;
  title: string;
  entityType: string;
  entityId: string;
  disablePreview?: boolean;
  eInvoiceSwitch?: ReactNode;
  operationPending?: boolean;
  language?: Language | null;
  onLanguageChange?: (language: Language) => void;
}> = ({
  previewUrl,
  title,
  entityType,
  entityId,
  disablePreview = false,
  eInvoiceSwitch,
  operationPending = false,
  language = 'german',
  onLanguageChange,
}) => {
  const documentContext = useContext(DocumentContext);
  const { sm } = useContext(WindowContext);
  const { id: eventId } = useParams();
  const { i18n } = useLingui();
  const { ref, width } = useElementSize();
  const navigate: NavigateFunction = useNavigate();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [unresolvablePlaceholders, setUnresolvablePlaceholders] = useState<[]>();
  const [previewRequested, setPreviewRequested] = useState(false);

  const showPreview = () => {
    if (!unresolvablePlaceholders || unresolvablePlaceholders.length > 0) {
      fetchUnresolvablePlaceholders(true);
    } else {
      setPreviewRequested(true);
    }
  };

  useEffect(() => {
    if (previewRequested) {
      setLoading(true);
      if (!operationPending) {
        navigate(previewUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRequested, operationPending]);

  useEffect(() => {
    if (entityType && entityId) {
      fetchUnresolvablePlaceholders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const fetchUnresolvablePlaceholders = (notification?: boolean) => {
    setLoading(true);
    getUnresolvablePlaceholders(entityType, entityId)
      .then((unresolvablePlaceholders) => {
        setUnresolvablePlaceholders(unresolvablePlaceholders);
        if (notification && unresolvablePlaceholders.length > 0) {
          ToastS.warn('placeholder.unsatisfied', i18n._('placeholder.unsatisfied'));
          unresolvablePlaceholders.forEach((placeholder: any) => {
            const translation = i18n._(placeholder, { 0: 1 });
            ToastS.warn(`unresolvable.placeholder-${translation}`, i18n._(`${translation} nicht gesetzt`));
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const rootAnchorUrl = eventId ? `/events/${eventId}/documents` : '/documents';
  const previewDisabled = disablePreview || isLoading || (documentContext && documentContext.negativeSum);

  const languagePicker =
    entityType === 'documents' ? (
      <DocumentLanguagePicker language={language} onLanguageChange={onLanguageChange} />
    ) : null;

  return (
    <>
      {sm && (
        <>
          {entityType === 'documents' && eInvoiceSwitch && (
            <Group position="right" mb="sm">
              {eInvoiceSwitch}
            </Group>
          )}
          {entityType === 'contracts' && <EventContractHeaderActions showPreview={showPreview} />}
          {entityType === 'documents' && <DocumentActions showPreview={showPreview} />}
          {entityType === 'questionnaires' && <QuestionnaireActions id={Number(entityId)} showPreview={showPreview} />}
        </>
      )}
      {!sm && (
        <div ref={ref}>
          <Group position="apart">
            <Breadcrumbs>
              <Anchor onClick={() => navigate(rootAnchorUrl)}>Dateien</Anchor>
              <Anchor sx={{ pointerEvents: 'none' }}>
                <b>{title}</b>
              </Anchor>
            </Breadcrumbs>
            <Group position="apart">
              <Group>
                {eInvoiceSwitch && eInvoiceSwitch}
                <Group spacing="xs" noWrap>
                  {languagePicker}
                  {width > 500 ? (
                    <Button
                      size="xs"
                      disabled={previewDisabled}
                      leftIcon={isLoading ? <Loader size="xs" color="white" /> : <IconEye size={20} />}
                      onClick={showPreview}
                      data-testid={TestS.Navigate.Documents.TO_PREVIEW}
                    >
                      Vorschau
                    </Button>
                  ) : (
                    <ActionIcon
                      color="blue"
                      variant="filled"
                      size={30}
                      disabled={previewDisabled}
                      onClick={showPreview}
                      data-testid={TestS.Navigate.Documents.TO_PREVIEW}
                    >
                      <IconEye color="white" size={20} />
                    </ActionIcon>
                  )}
                </Group>
                {entityType === 'contracts' && <EventContractHeaderActions showPreview={showPreview} />}
                {entityType === 'documents' && <DocumentActions showPreview={showPreview} />}
                {entityType === 'questionnaires' && (
                  <QuestionnaireActions id={Number(entityId)} showPreview={showPreview} />
                )}
              </Group>
            </Group>
          </Group>
          <Divider my="sm" />
        </div>
      )}
    </>
  );
};

export default DocumentHeadTemplate;
